import { type ReactNode } from 'react';

import { BoardAddIcon, InboxIcon, MousePointerClickIcon } from '@air/design-icons';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';

const CREATE_NEW_CANVAS_DESCRIPTION = 'Edit, refine, and multiply with AI';
const CREATE_NEW_CANVAS_LABEL = 'Create new Canvas';

export type DescriptiveCreateMenuItem = {
  description: string;
  icon: ReactNode;
  id: string;
  label: string;
  onSelect: () => void;
};

export const toDescriptiveCreateMenuOptions = (items: DescriptiveCreateMenuItem[]): DropdownMenuOption[] => {
  const options: DropdownMenuOption[] = [];

  items.forEach((item, index) => {
    if (index > 0) {
      options.push({
        id: `${item.id}-separator`,
        type: 'separator',
      });
    }

    options.push({
      description: item.description,
      id: item.id,
      label: item.label,
      onSelect: item.onSelect,
      prefix: item.icon,
      truncate: false,
      type: 'item',
    });
  });

  return options;
};

type GetAddBoardCreateMenuOptionsParams = {
  boardDescription: string;
  onCreateBoard: (() => void) | undefined;
  onCreateCanvas: (() => void) | undefined;
};

const getAddBoardCreateMenuOptions = ({
  boardDescription,
  onCreateBoard,
  onCreateCanvas,
}: GetAddBoardCreateMenuOptionsParams): DropdownMenuOption[] => {
  const items: DescriptiveCreateMenuItem[] = [];

  if (onCreateBoard) {
    items.push({
      description: boardDescription,
      icon: <BoardAddIcon className="ads:text-icon-subtle text-gray-9" size="sm" />,
      id: 'create-new-board',
      label: 'Add new board',
      onSelect: onCreateBoard,
    });
  }

  if (onCreateCanvas) {
    items.push({
      description: CREATE_NEW_CANVAS_DESCRIPTION,
      icon: <MousePointerClickIcon className="ads:text-icon-subtle text-gray-9" size="sm" />,
      id: 'create-new-canvas',
      label: CREATE_NEW_CANVAS_LABEL,
      onSelect: onCreateCanvas,
    });
  }

  return toDescriptiveCreateMenuOptions(items);
};

type GetScopedAddBoardCreateMenuOptionsParams = {
  onCreateBoard: (() => void) | undefined;
  onCreateCanvas: (() => void) | undefined;
};

export const getLibraryCreateMenuOptions = ({
  onCreateBoard,
  onCreateCanvas,
}: GetScopedAddBoardCreateMenuOptionsParams): DropdownMenuOption[] =>
  getAddBoardCreateMenuOptions({
    boardDescription: 'Add a board in this library',
    onCreateBoard,
    onCreateCanvas,
  });

export const getWorkspaceCreateMenuOptions = ({
  onCreateBoard,
  onCreateCanvas,
}: GetScopedAddBoardCreateMenuOptionsParams): DropdownMenuOption[] =>
  getAddBoardCreateMenuOptions({
    boardDescription: 'Add a board in this workspace',
    onCreateBoard,
    onCreateCanvas,
  });

export type GetBoardCreateMenuOptionsParams = {
  onAddSubBoard: (() => void) | undefined;
  onCollectContent: (() => void) | undefined;
  onCreateCanvas: (() => void) | undefined;
};

export const getBoardCreateMenuOptions = ({
  onAddSubBoard,
  onCollectContent,
  onCreateCanvas,
}: GetBoardCreateMenuOptionsParams): DropdownMenuOption[] => {
  const items: DescriptiveCreateMenuItem[] = [];

  if (onAddSubBoard) {
    items.push({
      description: 'Create a nested board to organize assets',
      icon: <BoardAddIcon className="ads:text-icon-subtle text-gray-9" size="sm" />,
      id: 'add-sub-board',
      label: 'Add sub-board',
      onSelect: onAddSubBoard,
    });
  }

  if (onCreateCanvas) {
    items.push({
      description: CREATE_NEW_CANVAS_DESCRIPTION,
      icon: <MousePointerClickIcon className="ads:text-icon-subtle text-gray-9" size="sm" />,
      id: 'create-new-canvas',
      label: CREATE_NEW_CANVAS_LABEL,
      onSelect: onCreateCanvas,
    });
  }

  if (onCollectContent) {
    items.push({
      description: 'Allow external contributors to upload',
      icon: <InboxIcon className="ads:text-icon-subtle text-gray-9" size="sm" />,
      id: 'content-collection',
      label: 'Collect content',
      onSelect: onCollectContent,
    });
  }

  return toDescriptiveCreateMenuOptions(items);
};
